import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotVal = (formData.get('website_alt') as string || '').trim();
    if (honeypotVal) {
      setSubmitStatus('success');
      setEmail('');
      return;
    }

    setSubmitStatus('loading');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          body.append(key, value as string);
        }
      });

      const resp = await fetch('https://readdy.ai/api/form/d958hrc41f1vs39a6ugg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await resp.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (resp.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormError('Произошла ошибка. Попробуйте позже.');
        } else {
          setFormError(serverMsg || 'Произошла ошибка. Попробуйте позже.');
        }
        setSubmitStatus('error');
      }
    } catch {
      setFormError('Ошибка соединения. Проверьте интернет.');
      setSubmitStatus('error');
    }
  };

  return (
    <footer className="px-4 md:px-8 py-14">
      <div className="max-w-7xl mx-auto bg-foreground-900 rounded-3xl px-8 md:px-14 py-14 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none select-none flex items-center justify-center opacity-[0.04]">
          <span className="font-heading text-[12rem] font-bold text-background-50 whitespace-nowrap leading-none">yoybuy</span>
        </div>

        <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          <div>
            <h3 className="font-heading text-4xl font-bold text-background-50 mb-6">YOYBUY</h3>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-5 font-body whitespace-nowrap">Категории</h4>
            <ul className="space-y-3">
              {categories.map((cat) => (
                <li key={cat.slug}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-300 hover:text-background-50 transition-colors font-body whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-5 font-body whitespace-nowrap">Мы в соцсетях</h4>
            <ul className="space-y-3">
              <li>
                <a href="#" className="flex items-center gap-3 text-sm text-foreground-300 hover:text-background-50 transition-colors font-body group whitespace-nowrap">
                  <span className="w-5 h-5 flex items-center justify-center border border-foreground-400 rounded-full group-hover:border-background-50 transition-colors">
                    <i className="ri-instagram-line text-xs"></i>
                  </span>
                  Instagram
                </a>
              </li>
              <li>
                <a href="#" className="flex items-center gap-3 text-sm text-foreground-300 hover:text-background-50 transition-colors font-body group whitespace-nowrap">
                  <span className="w-5 h-5 flex items-center justify-center border border-foreground-400 rounded-full group-hover:border-background-50 transition-colors">
                    <i className="ri-youtube-line text-xs"></i>
                  </span>
                  YouTube
                </a>
              </li>
              <li>
                <a href="#" className="flex items-center gap-3 text-sm text-foreground-300 hover:text-background-50 transition-colors font-body group whitespace-nowrap">
                  <span className="w-5 h-5 flex items-center justify-center border border-foreground-400 rounded-full group-hover:border-background-50 transition-colors">
                    <i className="ri-facebook-line text-xs"></i>
                  </span>
                  Facebook
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-5 font-body whitespace-nowrap">Подписка</h4>
            <p className="text-sm text-foreground-300 mb-4 font-body leading-relaxed">
              Получайте новости о новых поступлениях и скидках
            </p>
            <form onSubmit={handleSubmit} data-readdy-form="" className="flex">
              <input
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Ваш email"
                className="flex-1 bg-foreground-800 border border-foreground-700 rounded-l-full px-4 py-2.5 text-sm text-background-50 placeholder:text-foreground-400 outline-none focus:border-background-300 transition-colors font-body"
              />
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <button
                type="submit"
                disabled={submitStatus === 'loading'}
                className="bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 rounded-r-full px-4 py-2.5 flex items-center justify-center transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={`${submitStatus === 'loading' ? 'ri-loader-4-line animate-spin' : 'ri-arrow-right-line'} w-4 h-4 flex items-center justify-center`}></i>
              </button>
            </form>
            {submitStatus === 'success' && (
              <p className="mt-2 text-xs text-accent-400 font-body">Спасибо за подписку!</p>
            )}
            {submitStatus === 'error' && formError && (
              <p className="mt-2 text-xs text-red-400 font-body">{formError}</p>
            )}
          </div>
        </div>

        <div className="relative z-10 mt-12 pt-6 border-t border-foreground-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500 font-body">
            &copy; {new Date().getFullYear()} YOYBUY. Все права защищены.
          </p>
          <Link to="/" className="text-xs text-foreground-500 hover:text-background-50 transition-colors font-body whitespace-nowrap">
            Главная страница
          </Link>
        </div>
      </div>
    </footer>
  );
}